<template>
  <div class="list-container">
    <div class="top-fix-area" ref="topFixArea">
      <h1 class="page-title">ユーザ一覧</h1>
      <div class="mb-2">
        <router-link v-if="isAdmin" :to="{ path: '/create-user', query: getSearchQuery() }" class="btn btn-sm btn-primary me-2">
          ユーザ作成
        </router-link>

        <button v-if="isAdmin" type="button" class="btn btn-sm btn-primary me-2" data-bs-toggle="modal" data-bs-target="#exampleModal">
          CSVインポート
        </button>

        <button type="button" class="btn btn-sm btn-primary me-2" @click="downloadCsv">
          CSVエクスポート
        </button>

        <button class="btn btn-sm btn-outline-secondary" @click="isSearchExpanded = !isSearchExpanded">
          {{ isSearchExpanded ? '検索条件を非表示' : '検索条件を表示' }}
        </button>
      </div>

      <div class="modal fade" id="exampleModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h1 class="modal-title fs-5">CSVインポート</h1>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="clearErrorMessages"></button>
            </div>
            <div class="modal-body">
              <div v-if="errorMessages">
                <div class="error-massage-item" v-for="errorMessage in errorMessages" :key="errorMessage.id">
                  <p class="alert alert-danger mt-2">{{ errorMessage.row_num }}行目：{{ errorMessage.messages[0] }}</p>
                </div>
              </div>

              <div class="my-3">
                <p>ユーザ情報CSVファイル選択</p>
                <input id="userCsvFileInput" type="file" class="form-control" @change="handleFileChange">
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="clearErrorMessages">閉じる</button>
              <button type="button" class="btn btn-primary" @click="submitFile">インポート</button>
            </div>
          </div>
        </div>
      </div>

      <SearchPanelWrapper :key="route.fullPath" v-model="isSearchExpanded" :getTableElement="() => tableWrapper?.$el || tableWrapper?.value">
        <form class="search-container" @submit.prevent="searchUsers">
          <div class="row g-3 align-items-center">
            <div class="col-auto">
              <label for="searchName" class="col-form-label">ユーザ名</label>
            </div>
            <div class="col-auto">
              <input id="searchName" v-model="searchName" type="text" class="form-control" placeholder="ユーザ名で検索">
            </div>

            <div class="col-auto">
              <label for="searchEmail" class="col-form-label">メールアドレス</label>
            </div>
            <div class="col-auto">
              <input id="searchEmail" v-model="searchEmail" type="text" class="form-control" placeholder="メールアドレスで検索">
            </div>

            <DeletedStatusSelect v-model="searchDeletedStatus" id="userDeletedStatus" />

            <div class="col-auto">
              <button type="submit" class="btn btn-sm btn-primary mx-1">検索</button>
              <button type="button" class="btn btn-sm btn-secondary mx-1" @click="searchResetUsers">クリア</button>
            </div>
          </div>
        </form>
      </SearchPanelWrapper>
    </div>

    <div class="d-flex justify-content-end" ref="paginationContainer">
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @change-page="handleChangePage"
      />
    </div>

    <div class="table-wrapper" ref="tableWrapper">
      <table class="master-table table table-bordered">
        <thead class="table-dark">
          <tr>
            <th class="text-nowrap">ID</th>
            <th class="text-nowrap">ユーザ名</th>
            <th class="text-nowrap">メールアドレス</th>
            <th class="text-nowrap">現在システム権限</th>
            <th class="text-nowrap">システム別権限</th>
            <th class="btn-cell"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id" :class="{ 'no-active-user': user.del_flg == 1 }">
            <td>{{ user.id }}</td>
            <td>{{ user.name }}</td>
            <td>{{ user.email }}</td>
            <td>{{ roleLabel(user.role) }}</td>
            <td>
              <div v-for="clientSystem in clientSystems" :key="`${user.id}-${clientSystem.key}`">
                {{ clientSystem.label }}: {{ roleLabel(user.roles_by_system?.[clientSystem.key]) }}
              </div>
            </td>
            <td class="btn-cell text-nowrap">
              <button v-if="isAdmin" class="btn btn-sm btn-success me-2" data-bs-toggle="modal" data-bs-target="#passwordUpdateModal" @click="handlePasswordUpdateClick(user.id)">
                パスワード更新
              </button>

              <router-link :to="{ name: 'UserDetail', params: { id: user.id }, query: getSearchQuery() }">
                <button type="button" class="btn btn-sm btn-info me-2">詳細</button>
              </router-link>

              <router-link v-if="isAdmin" :to="{ name: 'UserEdit', params: { id: user.id }, query: getSearchQuery() }">
                <button type="button" class="btn btn-sm btn-success me-2">編集</button>
              </router-link>

              <button
                v-if="user.del_flg == 0 && isAdmin"
                class="btn btn-sm btn-danger"
                data-bs-toggle="modal"
                data-bs-target="#deleteModal"
                @click="handleDeleteClick(user.id)"
              >
                削除
              </button>
              <button
                v-if="user.del_flg == 1 && isAdmin"
                class="btn btn-sm btn-warning"
                data-bs-toggle="modal"
                data-bs-target="#restoreModal"
                @click="handleRestoreClick(user.id)"
              >
                復元
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <PasswordResetModal :userId="paramsIdToPasswordUpdate" />

    <DeleteConfirmationModal
      :paramsId="paramsIdToDelete"
      @confirmDelete="handleConfirmDelete"
    />

    <RestoreConfirmationModal
      :paramsId="paramsIdToRestore"
      @confirmRestore="handleConfirmRestore"
    />
  </div>
</template>

<script>
import { ref, onMounted, watchEffect, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import {
  getUsers,
  getUserRoleCatalog,
  deleteUser,
  restoreUser,
  exportUsersCsv,
  importUsersCsv
} from '@/api/axios';
import { Modal } from 'bootstrap';
import SearchPanelWrapper from '@/components/searchPanels/SearchPanelWrapper.vue';
import PasswordResetModal from '@/components/shared/PasswordResetModal.vue';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal.vue';
import RestoreConfirmationModal from '@/components/shared/RestoreConfirmationModal.vue';
import Pagination from '@/components/shared/Pagination.vue';
import DeletedStatusSelect from '@/components/shared/DeletedStatusSelect.vue';
import { currentRoleFromStoreOrLocalStorage, isAdminRole } from '@/utils/roleAuthorization';

export default {
  components: {
    SearchPanelWrapper,
    PasswordResetModal,
    DeleteConfirmationModal,
    RestoreConfirmationModal,
    Pagination,
    DeletedStatusSelect
  },
  setup() {
    const title = ref('ユーザ一覧');

    watchEffect(() => {
      document.title = title.value;
    });

    const store = useStore();
    const isAdmin = computed(() => isAdminRole(currentRoleFromStoreOrLocalStorage(store.state.userRole)));
    const router = useRouter();
    const route = useRoute();

    const users = ref([]);
    const selectedFile = ref(null);
    const errorMessages = ref({});

    const paramsIdToPasswordUpdate = ref(null);
    const paramsIdToDelete = ref(null);
    const paramsIdToRestore = ref(null);

    const searchName = ref('');
    const searchEmail = ref('');
    const searchDeletedStatus = ref('active');
    const clientSystems = ref([]);
    const roleOptions = ref([]);

    const currentPage = ref(1);
    const totalPages = ref(1);

    const isSearchExpanded = ref(true);
    const topFixArea = ref(null);
    const tableWrapper = ref(null);
    const paginationContainer = ref(null);

    const getSearchQuery = () => ({
      user_name: searchName.value,
      user_email: searchEmail.value,
      user_deleted_status: searchDeletedStatus.value,
      user_page: currentPage.value || 1
    });

    const roleLabel = (roleValue) => {
      const roleOption = roleOptions.value.find((option) => Number(option.value) === Number(roleValue));
      return roleOption?.label || roleValue || '-';
    };

    const fetchUsers = async (page = 1) => {
      try {
        const response = await getUsers({
          name: searchName.value,
          email: searchEmail.value,
          deleted_status: searchDeletedStatus.value,
          page
        });

        users.value = response.data.users || [];
        currentPage.value = response.data.meta?.current_page || 1;
        totalPages.value = Math.max(response.data.meta?.total_pages || 1, 1);
        clientSystems.value = response.data.meta?.client_systems || clientSystems.value;
        roleOptions.value = response.data.meta?.role_options || roleOptions.value;
      } catch (error) {
        console.error('ユーザ情報データの取得に失敗しました:', error);
      }
    };

    const fetchRoleCatalog = async () => {
      try {
        const response = await getUserRoleCatalog();
        clientSystems.value = response.data.client_systems || clientSystems.value;
        roleOptions.value = response.data.role_options || roleOptions.value;
      } catch (error) {
        console.error('権限マスタ情報の取得に失敗しました:', error);
      }
    };

    const handleChangePage = async (page) => {
      if (page < 1 || page > totalPages.value) return;

      await fetchUsers(page);

      const q = getSearchQuery();
      q.user_page = page;
      router.replace({ path: '/users', query: q });

      if (tableWrapper.value) {
        tableWrapper.value.scrollTop = 0;
      }
    };

    const handlePasswordUpdateClick = (userId) => {
      paramsIdToPasswordUpdate.value = userId;
    };

    const handleDeleteClick = (userId) => {
      paramsIdToDelete.value = userId;
    };

    const handleRestoreClick = (userId) => {
      paramsIdToRestore.value = userId;
    };

    const handleConfirmDelete = async (userId) => {
      try {
        await deleteUser(userId);
        store.commit('setFlashMessage', { message: 'ユーザーが削除されました', type: 'success' });
        await fetchUsers(currentPage.value);
      } catch (error) {
        store.commit('setFlashMessage', { message: 'ユーザ情報削除に失敗しました', type: 'danger' });
      }
    };

    const handleConfirmRestore = async (userId) => {
      try {
        await restoreUser(userId);
        store.commit('setFlashMessage', { message: 'ユーザーが復元されました', type: 'success' });
        await fetchUsers(currentPage.value);
      } catch (error) {
        store.commit('setFlashMessage', { message: 'ユーザ情報復元に失敗しました', type: 'danger' });
      }
    };

    const downloadCsv = async () => {
      try {
        const response = await exportUsersCsv({
          name: searchName.value,
          email: searchEmail.value,
          deleted_status: searchDeletedStatus.value
        });

        const now = new Date();
        const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
        const fileName = `users_${timestamp}.csv`;

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('CSVダウンロードエラー', error);
      }
    };

    const handleFileChange = (event) => {
      selectedFile.value = event.target.files[0];
    };

    const closeModal = () => {
      const modalElement = document.getElementById('exampleModal');
      const modal = Modal.getInstance(modalElement) || new Modal(modalElement);
      modal.hide();
    };

    const clearErrorMessages = () => {
      errorMessages.value = {};
      selectedFile.value = null;

      const input = document.getElementById('userCsvFileInput');
      if (input) {
        input.value = '';
      }
    };

    const submitFile = async () => {
      if (!selectedFile.value) {
        store.commit('setFlashMessage', { message: 'ファイルが選択されていません', type: 'danger' });
        return;
      }

      try {
        await importUsersCsv(selectedFile.value);
        store.commit('setFlashMessage', { message: 'アップロードに成功しました', type: 'success' });
        closeModal();
        clearErrorMessages();
        await fetchUsers(currentPage.value);
      } catch (error) {
        if (error.response && error.response.data) {
          errorMessages.value = error.response.data || {};
        }
        console.error('アップロードに失敗しました', error);
        store.commit('setFlashMessage', { message: 'アップロードに失敗しました', type: 'danger' });
      }
    };

    const searchUsers = async () => {
      await fetchUsers(1);

      const q = getSearchQuery();
      q.user_page = 1;
      router.push({ path: '/users', query: q });
    };

    const searchResetUsers = async () => {
      searchName.value = '';
      searchEmail.value = '';
      searchDeletedStatus.value = 'active';
      await fetchUsers(1);

      const q = getSearchQuery();
      q.user_page = 1;
      router.replace({ path: '/users', query: q });
    };

    onMounted(async () => {
      const q = route.query;
      searchName.value = q.user_name || '';
      searchEmail.value = q.user_email || '';
      searchDeletedStatus.value = q.user_deleted_status || 'active';
      currentPage.value = q.user_page ? Number(q.user_page) : 1;
      await fetchRoleCatalog();
      await fetchUsers(currentPage.value);
    });

    return {
      title,
      isAdmin,
      route,
      users,
      paramsIdToPasswordUpdate,
      handlePasswordUpdateClick,
      paramsIdToDelete,
      paramsIdToRestore,
      handleDeleteClick,
      handleRestoreClick,
      handleConfirmDelete,
      handleConfirmRestore,
      downloadCsv,
      selectedFile,
      handleFileChange,
      clearErrorMessages,
      submitFile,
      errorMessages,
      searchName,
      searchEmail,
      searchDeletedStatus,
      clientSystems,
      roleLabel,
      getSearchQuery,
      currentPage,
      totalPages,
      handleChangePage,
      searchUsers,
      searchResetUsers,
      isSearchExpanded,
      topFixArea,
      tableWrapper,
      paginationContainer
    };
  }
};
</script>

<style scoped>
.list-container {
  padding: 20px 20px 80px 20px;
  margin: auto;
}

.table-wrapper {
  overflow: auto;
  max-height: calc(100vh - 320px);
}

.master-table .btn-cell {
  width: 296px;
  min-width: 296px;
  max-width: 296px;
}

.no-active-user td {
  background-color: #aaa;
}
</style>
